import React, { useEffect, useState } from "react";
import { Alert, Stack, Typography } from "@mui/material";
import { HostSelectInput, MeetingHost } from "../inputs";
import PrimaryButton from "../buttons/PrimaryButton";
import SecondaryButton from "../buttons/SecondaryButton";
import Dialog from "./Dialog";

export function AssignCoHostDialog({
  open,
  onClose,
  onConfirm,
  title = "Assign Co-Host",
  description,
  confirmLabel = "Assign",
  disableEmptySelection = false,
}: {
  open: boolean;
  onClose?: () => void;
  onConfirm: (value: { coHost: MeetingHost | null }) => void | Promise<void>;
  title?: string;
  description?: string;
  confirmLabel?: string;
  disableEmptySelection?: boolean;
}) {
  const [selectedCoHost, setSelectedCoHost] = useState<MeetingHost | null>(
    null,
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const selectedCoHostHasExpiredToken =
    selectedCoHost !== null &&
    (selectedCoHost.tokenInfo?.tokenType?.length ?? 0) > 0 &&
    selectedCoHost.authValid === false;

  useEffect(() => {
    if (!open) {
      setSelectedCoHost(null);
      setIsSubmitting(false);
      setSubmitError(null);
    }
  }, [open]);

  const handleConfirm = async () => {
    setSubmitError(null);
    setIsSubmitting(true);

    try {
      await onConfirm({
        coHost: selectedCoHost,
      });
    } catch (error) {
      console.error(error);
      setSubmitError(
        error instanceof Error && error.message
          ? error.message
          : "There was a problem setting co-host",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      title={title}
      open={open}
      onClose={isSubmitting ? undefined : onClose}
      actions={
        <>
          <PrimaryButton
            disabled={
              isSubmitting ||
              selectedCoHostHasExpiredToken ||
              (disableEmptySelection && selectedCoHost === null)
            }
            onClick={() => {
              void handleConfirm();
            }}
          >
            {confirmLabel}
          </PrimaryButton>
          <SecondaryButton disabled={isSubmitting} onClick={onClose}>
            Cancel
          </SecondaryButton>
        </>
      }
    >
      <Stack spacing={2} sx={{ pt: 1, minHeight: "5rem" }}>
        {description && <Typography>{description}</Typography>}
        <HostSelectInput
          host={selectedCoHost}
          onHostSelect={(host) => {
            setSelectedCoHost(host);
            setSubmitError(null);
          }}
          label="Co-Host Email"
          disablePortal={false}
        />
        {selectedCoHostHasExpiredToken && (
          <Alert severity="warning">
            Selected user has an expired calendar connection. Please select a
            different user.
          </Alert>
        )}
        {submitError && (
          <Typography color="error" variant="body2">
            {submitError}
          </Typography>
        )}
      </Stack>
    </Dialog>
  );
}
